import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import dayjs from 'dayjs';
import { Button } from '@cropler/ui-kit';

import { Icon as CroplerIcon } from '@cropler/ui-kit';

import styles from './styles.module.scss';

import { BreakpointTablet } from '../../constants/ui';
import { useViewport } from '../../hooks';
import { selectDate } from '../../store/slices/sensors';
import { DateFormat } from '../../constants/data';
import { TimelineListImage } from './index.jsx';
import { GetSliderImages } from './lib/index.jsx';

const TimelineList = ({ t, dates, data = [], dataCompared = [] }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { widthViewport } = useViewport();
  const dispatch = useDispatch();
  const listRef = useRef(null);

  const { selectedDate, selectedSensor, selectedSecondarySensor, photosType } = useSelector((state) => state.sensors);
  const { notesList } = useSelector((state) => state.notes);

  const today = dayjs().format(DateFormat);

  const [scrollIndex, setScrollIndex] = useState(null);
  const [currentMonth, setCurrentMonth] = useState('');
  const [currentSensor, setCurrentSensor] = useState('');
  const [currentDatesCount, setCurrentDatesCount] = useState(null);

  const images = useMemo(
    () =>
      GetSliderImages({
        t,
        dates: dates,
        imagesSelected: data,
        imagesCompared: dataCompared,
        photosType,
      }),
    [dataCompared],
  );

  // scroll to the current image
  useEffect(() => {
    if (!listRef || !selectedSensor || selectedSensor === currentSensor || !dates || dates.length === currentDatesCount)
      return;
    const indexImage = images.findIndex((d) => selectedDate === d.date);
    setScrollIndex(indexImage > 0 ? indexImage : 0);
    setCurrentDatesCount(dates.length);
    setCurrentSensor(selectedSensor);

    if (listRef && listRef.current && indexImage !== null) {
      setTimeout(() => {
        listRef.current.scrollToIndex({ index: indexImage, align: 'end', offset: 120, behavior: 'smooth' });
      }, 200);
    }
  }, [dates, selectedSensor, listRef]);

  useEffect(() => {
    if (dataCompared.length > 0) {
      if (listRef && listRef.current) {
        const indexImage = dates.findIndex((item) => selectedDate === item);
        listRef.current.scrollToIndex({ index: indexImage, align: 'end', offset: 64 });
      }
    }
  }, [dataCompared]);

  const onSelectDate = ({ date }) => {
    dispatch(selectDate(date));
    searchParams.set('date', date);
    setSearchParams(searchParams, { replace: true });

    const indexImage = images.findIndex((item) => date === item.date);
    setScrollIndex(indexImage);
  };

  const onClickNextDate = () => {
    const nextDate = images[scrollIndex + 1].date;
    setScrollIndex(scrollIndex + 1);
    onSelectDate({ date: nextDate });
  };

  const onClickPrevDate = () => {
    const nextDate = images[scrollIndex - 1].date;
    setScrollIndex(scrollIndex - 1);
    onSelectDate({ date: nextDate });
  };

  // Get sticky month
  const onSliderRangeChange = ({ startIndex }) => {
    const date = images[startIndex]?.date;
    const month = dayjs(date).format('MMM');
    if (month !== currentMonth) {
      setCurrentMonth(month);
    }
  };

  return (
    <div className={styles.sliderContainer}>
      <div className={styles.sliderMonthTop}>{currentMonth}</div>
      {images.length > 0 && (scrollIndex || scrollIndex === 0) && (
        <>
          {/* Prev/Next buttons */}
          <Button
            type="primary"
            icon={<CroplerIcon name="left" />}
            className={styles.sliderPrev}
            disabled={selectedDate === images[0].date}
            onClick={() => onClickPrevDate()}
          ></Button>
          <Button
            type="primary"
            icon={<CroplerIcon name="left" />}
            className={styles.sliderNext}
            disabled={selectedDate === images[images.length - 1].date}
            onClick={() => onClickNextDate()}
          ></Button>

          <Virtuoso
            // key={selectedSensor + 'f'}
            ref={listRef}
            className={styles.slider}
            style={{ height: widthViewport > BreakpointTablet ? 100 : 100 }}
            data={images}
            horizontalDirection
            initialTopMostItemIndex={{ index: scrollIndex, align: 'end', offset: 120 }}
            // initialTopMostItemIndex={scrollIndex}
            itemContent={(_, item) => (
              <div className={`${styles.sliderItem} ${item.disabled && styles.sliderItemDisabled}`}>
                <button className={styles.sliderItemButton} onClick={() => onSelectDate({ date: item.date })}></button>
                <div className={styles.sliderMonth}>{currentMonth !== item.dateMonth && item.dateMonth}</div>
                <time
                  className={`
                ${styles.sliderDate} ${item.date === selectedDate && styles.sliderDateSelected}
                ${item.date === today && styles.sliderDateToday}
                `}
                >
                  {dayjs(item.date).date()}
                </time>
                <div className={styles.sliderImg}>
                  {item.thumbnail && <TimelineListImage src={item.thumbnail} alt={item.date} />}
                  {selectedDate !== item.date && <div className={styles.sliderImgShadow}></div>}
                  {!item.thumbnail && <CroplerIcon name="emptyTimeline" />}
                </div>
                {selectedSecondarySensor && (
                  <div className={styles.sliderImg}>
                    {item.thumbnailCompared && <TimelineListImage src={item.thumbnailCompared} alt={item.date} />}
                    {selectedDate !== item.date && <div className={styles.sliderImgShadow}></div>}
                    {!item.thumbnailCompared && <CroplerIcon name="emptyTimeline" />}
                  </div>
                )}
                {!selectedSecondarySensor && notesList && notesList.find((n) => n.date === item.date) && (
                  <div
                    className={styles.sliderItemNote}
                    style={{ backgroundColor: `rgba(var(--attention-rgb), ${item.date === selectedDate ? 1 : 0.5})` }}
                  ></div>
                )}
              </div>
            )}
            rangeChanged={onSliderRangeChange}
          />
        </>
      )}
    </div>
  );
};

export default TimelineList;
